import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { type FC, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import {
  type ICalculatedData,
  useMarketCalculator,
} from "~/feature/event/providers/MarketCalculatorProvider";
import { formatPercentageValue } from "~/utils/formatValue";

const APY_TOOLTIP =
  "Estimated yearly return. This figure is highly sensitive to the resolution date. Faster resolution = Higher APY. This is an informative projection, not a guaranteed rate of return.";
const HIGH_PROJECTED_APY_TOOLTIP =
  "This typically indicates a market nearing immediate resolution or significant volatility. High-yield projections in short-term markets are sensitive to minor price shifts and do not represent a guaranteed long-term rate of return.";
const APY_HIDDEN_TITLE_TOOLTIP = "Why is APY hidden?";
const APY_HIDDEN_TOOLTIP =
  "This trade’s payoff and timeframe produce an inflated annualized return.\nAPY assumes repeatable compounding, which doesn’t apply here.\nFocus on ROI and probability instead.";

const HIGH_APY = 500;
const HIDDEN_APY = 10_000;

const iconStyles = { fontSize: "11px", margin: "2px 0 0 2px" };
const iconLeftStyles = { fontSize: "11px", margin: "2px 2px 0 0" };

interface IMarketApyProps {
  calculatedData?: ICalculatedData;
}

export const MarketApy: FC<IMarketApyProps> = ({ calculatedData: calculatedDataProp }) => {
  const { calculatedData: contextCalculatedData } = useMarketCalculator();
  const calculatedData = calculatedDataProp ?? contextCalculatedData;

  const apy = useMemo(() => {
    if (calculatedData.daysRemaining < 1) {
      return "N/A";
    }
    return formatPercentageValue(calculatedData.apy);
  }, [calculatedData.daysRemaining, calculatedData.apy]);

  if (calculatedData.apy > HIDDEN_APY) {
    return (
      <>
        {"\n"}
        APY not meaningful
        <RichTooltip
          title={APY_HIDDEN_TOOLTIP}
          textTitle={APY_HIDDEN_TITLE_TOOLTIP}
        >
          <InfoOutlinedIcon color="disabled" sx={iconStyles} />
        </RichTooltip>
      </>
    );
  }

  if (calculatedData.apy > HIGH_APY) {
    return (
      <>
        {"\n"}
        <WarningIcon color="disabled" sx={iconLeftStyles} />
        High Projected APY: <strong>{apy}</strong>
        <RichTooltip title={HIGH_PROJECTED_APY_TOOLTIP}>
          <InfoOutlinedIcon color="disabled" sx={iconStyles} />
        </RichTooltip>
      </>
    );
  }

  return (
    <>
      {" | "}APY: <strong>{apy}</strong>
      <RichTooltip title={APY_TOOLTIP}>
        <InfoOutlinedIcon color="disabled" sx={iconStyles} />
      </RichTooltip>
    </>
  );
};
